import {
  IconCheck,
  IconPhoto,
  IconPlayerSkipForward,
  IconQuestionMark,
  IconX,
} from "@tabler/icons-react";
import type { CorrectAnswer } from "@labeling-tools/api/field-rep/generated/types.gen";
import { MISSING_IMAGE_PLACEHOLDER } from "@/config/constants";
import { productImageCache, useCachedImageUrl } from "@/utils/imageCache";
import { ProductProposal } from "@/types/trackLabeling";
import { toProductSetProposal } from "@/utils/productSet";
import { CorrectAnswerMessage, LabelerAnswer } from "./correctAnswer";

export type AnswerSide = "labeler" | "correct";

/** One side of the comparison: a product, or a Skip and what it meant. */
export type AnswerCardContent =
  | { kind: "product"; product: ProductProposal }
  | { kind: "skip"; description: string };

export const ANSWER_SIDES = {
  labeler: {
    label: "Your answer",
    Icon: IconX,
    chipClassName: "bg-[#FEF2F2] text-[#B91C1C]",
    borderClassName: "border-[#FCA5A5]",
    highlightClassName: "bg-[#FEF2F2]",
  },
  correct: {
    label: "Correct answer",
    Icon: IconCheck,
    chipClassName: "bg-[#F0FDF4] text-[#15803D]",
    borderClassName: "border-[#86EFAC]",
    highlightClassName: "bg-[#F0FDF4]",
  },
} as const;

export const toLabelerAnswerCardContent = (
  labelerAnswer: LabelerAnswer,
): AnswerCardContent => {
  switch (labelerAnswer.kind) {
    case "product":
      return { kind: "product", product: labelerAnswer.product };
    case "productNotListed":
      return {
        kind: "skip",
        description: "You didn't pick a product",
      };
    case "badDetection":
      return {
        kind: "skip",
        description: "You skipped the tracking step",
      };
  }
};

export const toCorrectAnswerCardContent = (
  correctAnswer: CorrectAnswer,
): AnswerCardContent =>
  correctAnswer.product
    ? { kind: "product", product: toProductSetProposal(correctAnswer.product) }
    : { kind: "skip", description: "Skip: no product matches" };

const ProductImage = ({ product }: { product: ProductProposal }) => {
  // The URL the grid already showed, so the browser serves the image from its cache.
  const imageUrl = useCachedImageUrl(
    productImageCache,
    product.product_id,
    product.imageUrl,
  );

  return (
    <div className="flex h-[100px] w-[120px] shrink-0 items-center justify-center overflow-hidden rounded-[14px] border border-[#F0F3F5] bg-[#FAFBFC] p-2">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={product.title}
          className="h-full w-full object-contain"
          onError={(event) => {
            const imageTarget = event.currentTarget;
            imageTarget.onerror = null;
            imageTarget.src = MISSING_IMAGE_PLACEHOLDER;
          }}
        />
      ) : (
        <IconPhoto size={32} className="text-[#CBD5E1]" />
      )}
    </div>
  );
};

const ProductDetails = ({
  product,
  highlightedField,
  highlightClassName,
}: {
  product: ProductProposal;
  highlightedField: CorrectAnswerMessage["differingField"];
  highlightClassName: string;
}) => {
  // Title when the product has no cluster.
  if (!product.cluster) {
    return (
      <span className="text-[14px] font-bold leading-snug text-[#1e293b]">
        {product.title}
      </span>
    );
  }

  // Otherwise try to highlight the difference.
  const highlight = (field: CorrectAnswerMessage["differingField"]) =>
    field === highlightedField
      ? `-mx-1.5 rounded-md px-1.5 ${highlightClassName}`
      : "";

  return (
    <>
      <span
        className={`text-[12px] font-medium text-[#94A3B8] ${highlight("brand")}`}
      >
        {product.cluster.brand.name}
      </span>
      <span
        className={`text-[14px] font-bold text-[#1e293b] ${highlight("cluster")}`}
      >
        {product.cluster.name}
      </span>
      {product.variant && (
        <span
          className={`text-[22px] font-extrabold text-[#1e293b] ${highlight("variant")}`}
        >
          {product.variant}
        </span>
      )}
    </>
  );
};

export /** "Your answer" or "Correct answer", in the side's colour. */
const AnswerChip = ({ side }: { side: AnswerSide }) => {
  const { label, Icon, chipClassName } = ANSWER_SIDES[side];

  return (
    <span
      className={`inline-flex items-center gap-1 self-start rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em] ${chipClassName}`}
    >
      <Icon size={12} stroke={3} />
      {label}
    </span>
  );
};

export const AnswerCard = ({
  side,
  content,
  differingField,
}: {
  side: AnswerSide;
  content: AnswerCardContent;
  differingField: CorrectAnswerMessage["differingField"];
}) => {
  const { borderClassName, highlightClassName } = ANSWER_SIDES[side];

  return (
    <div
      className={`flex min-h-[132px] items-center gap-4 rounded-[20px] border-2 bg-white p-4 ${borderClassName}`}
    >
      {content.kind === "product" ? (
        <>
          <ProductImage product={content.product} />
          <div className="flex min-w-0 flex-col items-start gap-2">
            <AnswerChip side={side} />
            <div className="flex flex-col items-start gap-0.5">
              <ProductDetails
                product={content.product}
                highlightedField={differingField}
                highlightClassName={highlightClassName}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex h-[100px] w-[120px] shrink-0 flex-col items-center justify-center gap-2 rounded-[14px] border border-dashed border-[#E2E8F0] bg-[#FAFBFC]">
            <IconPlayerSkipForward size={28} className="text-[#94A3B8]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#64748b]">
              Skip
            </span>
          </div>
          <div className="flex min-w-0 flex-col items-start gap-2">
            <AnswerChip side={side} />
            <span className="text-[15px] font-bold text-[#1e293b]">
              {content.description}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * The correct answer's placeholder before revealing to the labeler: a grey "Correct answer" chip and "???".
 * It fills the space of the card it covers.
 */
export const CoveredAnswerCard = () => (
  <div className="flex h-full items-center gap-4 rounded-[20px] border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-4">
    <div className="flex h-[100px] w-[120px] shrink-0 items-center justify-center rounded-[14px] bg-[#EEF2F6] text-[#94A3B8]">
      <IconQuestionMark size={36} />
    </div>
    <div className="flex min-w-0 flex-col items-start gap-2">
      <span className="inline-flex items-center gap-1 self-start rounded-full bg-[#EEF2F6] px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#64748b]">
        <IconCheck size={12} stroke={3} />
        {ANSWER_SIDES.correct.label}
      </span>
      <span className="text-[22px] font-extrabold tracking-[0.2em] text-[#94A3B8]">
        <span aria-hidden="true">???</span>
        <span className="sr-only">Hidden</span>
      </span>
    </div>
  </div>
);
